import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Tooltip } from '@travel/ui';
import { cx } from '@travel/utils';

import useOutsideClickWithEscape from 'hooks/useOutsideClickWithEscape';

import styles from './toolTip.module.scss';

import type { CSSProperties, ReactNode } from 'react';
type Props = {
  children: ReactNode;
  className?: string;
  /** ToolTip Label */
  label: ReactNode;
  /** Tooltip position based on the anchor element */
  position?: 'top' | 'bottom';
  /* hide the tooltip after a timer */
  delay?: number;
  wrapperClassName?: string;
};

function ReviewToolTip(props: Props) {
  const {
    className,
    label,
    children,
    position = 'bottom',
    delay,
    wrapperClassName,
    ...rest
  } = props;

  const [isDisplay, setIsDisplay] = useState(false);
  const [arrowOffset, setArrowOffset] = useState({ top: 0, left: 0 });
  const [tooltipOffset, setTooltipOffset] = useState({ top: 0, left: 0 });
  const timeoutId = useRef<number | null>(null);
  const anchorRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isSSR = typeof window === 'undefined';

  const viewportWidth = isSSR ? 0 : window.innerWidth;
  const isBottom = position === 'bottom';

  const handleOnToggleTooltip = (event) => {
    event.preventDefault();
    setIsDisplay((prev) => !prev);

    if (anchorRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const left = anchorRect.left + (isSSR ? 0 : window.scrollX);

      // Compute arrow offset (anchor center - tooltip left)
      const anchorCenter = anchorRect.left + anchorRect.width / 2;
      const tooltipLeft = left;
      const arrowLeftOffset = anchorCenter - tooltipLeft;

      setArrowOffset({ ...arrowOffset, left: arrowLeftOffset });
    }
  };

  useOutsideClickWithEscape(tooltipRef, anchorRef, () => {
    setIsDisplay(false);
  });
  useEffect(() => {
    if (delay && isDisplay) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }

      timeoutId.current = window.setTimeout(() => {
        setIsDisplay(false);
      }, delay);
    }

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [isDisplay, delay]);
  //Check if Tooltip has overflow (calculate overflow)
  useLayoutEffect(() => {
    const tooltipElement = tooltipRef.current?.children[0];
    const anchorElement = anchorRef.current?.children[0];

    if (!tooltipElement || !anchorElement) return;

    const tooltipRect = tooltipElement.getBoundingClientRect();

    // Using offsetHeight for dimensions will be consistent across Safari/Chrome
    const tooltipHeight = (tooltipElement as HTMLElement).offsetHeight;

    // Margin/buffer between the Tooltip and the edge of the viewport.
    const tooltipMargin = 8;

    const overflowLeft =
      viewportWidth - tooltipMargin - (tooltipRect?.left ?? 0) - (tooltipRect?.width ?? 0);
    const toolTipLeftOffset = overflowLeft < 0 ? overflowLeft : 0;

    const spacing = 8;

    const tooltipTopOffset =
      tooltipHeight + spacing;

    const arrowTopOffset = isBottom ? 0 : tooltipHeight;
    const arrowLeftOFfset = arrowOffset.left + Math.abs(toolTipLeftOffset);

    setTooltipOffset({ top: tooltipTopOffset, left: toolTipLeftOffset });
    setArrowOffset({ top: arrowTopOffset, left: arrowLeftOFfset });
  }, [arrowOffset.left, isBottom, isDisplay, viewportWidth]);

  return (
    <div className={cx(styles.tooltipWrapper, wrapperClassName)} ref={anchorRef} {...rest}>
      <span
        onClick={handleOnToggleTooltip}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOnToggleTooltip(e);
          }
        }}
        role="button"
        tabIndex={0}
        className={styles.tooltipTrigger}
        data-testid="tooltip-anchor"
        aria-label="tooltip-anchor"
      >
        {children}
      </span>
      {isDisplay && (
        <div
          ref={tooltipRef}
          className={styles.tooltipRef}
          style={
            {
              '--tooltip-left': `${tooltipOffset.left}px`,
              ...(!isBottom && { top: `-${tooltipOffset.top}px` }),
            } as CSSProperties
          }
          data-testid="tooltip-popup"
        >
          <Tooltip
            className={cx(styles.toolTip, !isBottom ? styles.tooltipOnTop : '', className)}
            style={
              {
                '--tooltip-left-after': `${arrowOffset.left}px`,
                '--tooltip-top-after': `${arrowOffset.top}px`,
              } as CSSProperties
            }
          >
            <span className={styles.label}>{label}</span>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default ReviewToolTip;
